import useInfrastructure from '@/application/useInfrastructure';
import {
  applyPercentageToTick,
  calculatePriceFromTick,
  calculateTickFromPrice,
  roundTickToSpacing,
} from '@/domain/ticks/helpers';
import {
  Address,
  FeeTickSpacing,
  FeeTier,
  RFBR_MAX_TICK,
  RFBR_MIN_TICK,
  getChainConstant,
} from 'nftx.js';
import { useCallback } from 'react';
import { formatEther, parseEther } from 'viem';

// UI utility to show percentages
export const getMinPercentages = (poolType: 'pair' | 'eth' | 'nft') => {
  if (poolType === 'nft') {
    return [0.1, 0.25, 0.5];
  }

  if (poolType === 'eth') {
    return [0.9, 0.75, 0.5];
  }

  // pair
  return [0.5, 0.25, 0.1];
};

export const getMaxPercentages = (poolType: 'pair' | 'eth' | 'nft') => {
  if (poolType === 'nft') {
    return [1, 2.5, 5];
  }

  if (poolType === 'eth') {
    return [0.2, 0.1, 0.01];
  }

  // pair
  return [1, 2.5, 5];
};

export const getPolaritySign = (
  input: 'minPriceInEth' | 'maxPriceInEth',
  poolType: 'pair' | 'eth' | 'nft'
) => {
  if (input === 'minPriceInEth') {
    if (poolType === 'pair' || poolType === 'eth') {
      return '-';
    }
    // nft
    return '+';
  }

  if (input === 'maxPriceInEth') {
    if (poolType === 'pair' || poolType === 'nft') {
      return '+';
    }
    // eth
    return '-';
  }
};

export const checkIsInvalid = (
  minPrice: string,
  maxPrice: string,
  price: string,
  isEthOnly: boolean,
  isNftOnly: boolean
) => {
  const invalidRange = Number(minPrice) >= Number(maxPrice);
  const invalidEth =
    isEthOnly &&
    (Number(minPrice) > Number(price) || Number(maxPrice) > Number(price));

  const invalidNft =
    isNftOnly &&
    (Number(maxPrice) < Number(price) || Number(minPrice) < Number(price));

  const isInvalid = invalidRange || invalidEth || invalidNft;

  return { invalidRange, invalidEth, invalidNft, isInvalid };
};

export function getLiquidityPercentage(
  amountMax: bigint | undefined,
  tokenAmount: string | undefined
) {
  if (tokenAmount === '') {
    return null;
  }
  if (amountMax && tokenAmount) {
    const tokenAmountEther = parseEther(tokenAmount);
    if (tokenAmountEther === 0n) {
      return 0;
    }

    return (
      Number(formatEther(tokenAmountEther)) / Number(formatEther(amountMax))
    );
  }

  return null;
}

export const computeTick = ({
  price,
  tickSpacing,
  isVToken0,
}: {
  price: string;
  tickSpacing: FeeTickSpacing;
  isVToken0: boolean;
}) => {
  if (!price) {
    return '';
  }
  return calculateTickFromPrice(
    parseEther(price),
    tickSpacing,
    isVToken0
  ).toString();
};

export const computeLowerTick = ({
  minPriceInEth,
  minPercent,
  tick,
  tickSpacing,
  isNftOnly,
  fullRange,
  network,
  isVToken0,
}: {
  minPriceInEth?: string;
  minPercent?: number;
  tick: string;
  tickSpacing: FeeTickSpacing;
  isNftOnly: boolean;
  fullRange: boolean;
  network: number;
  isVToken0: boolean;
}) => {
  if (!minPriceInEth && minPercent == null) {
    return 0;
  }
  if (fullRange) {
    return roundTickToSpacing(
      getChainConstant(RFBR_MIN_TICK, network),
      tickSpacing
    );
  }
  if (minPriceInEth) {
    return calculateTickFromPrice(
      parseEther(minPriceInEth),
      tickSpacing,
      isVToken0
    );
  }
  if (minPercent != null) {
    // if we're on an NFT-only pool the minPrice has to be above the current price
    const polarity = isNftOnly ? 'positive' : 'negative';
    return applyPercentageToTick(
      tick,
      tickSpacing,
      Math.floor(minPercent * 100),
      polarity,
      isVToken0
    );
  }
  return 0;
};

export const computeUpperTick = ({
  maxPriceInEth,
  maxPercent,
  tick,
  isEthOnly,
  tickSpacing,
  fullRange,
  network,
  isVToken0,
}: {
  maxPriceInEth?: string;
  maxPercent?: number;
  tick: string;
  tickSpacing: FeeTickSpacing;
  isEthOnly: boolean;
  fullRange: boolean;
  network: number;
  isVToken0: boolean;
}) => {
  if (!maxPriceInEth && maxPercent == null) {
    return 0;
  }
  if (fullRange) {
    return roundTickToSpacing(
      getChainConstant(RFBR_MAX_TICK, network),
      tickSpacing
    );
  }
  if (maxPriceInEth) {
    return calculateTickFromPrice(
      parseEther(maxPriceInEth),
      tickSpacing,
      isVToken0
    );
  }
  if (maxPercent != null) {
    const polarity = isEthOnly ? 'negative' : 'positive';

    return applyPercentageToTick(
      tick,
      tickSpacing,
      Math.floor(maxPercent * 100),
      polarity,
      isVToken0
    );
  }
  return 0;
};

export const computeMinPercent = ({
  tick: _tick,
  tickLower,
}: {
  tick: string;
  tickLower: number;
}) => {
  const tick = Number(_tick);
  if (!tick) {
    return 0;
  }
  return tickLower / tick;
};

export const computeMaxPercent = ({
  tick: _tick,
  tickUpper,
  isVToken0,
}: {
  tick: string;
  tickUpper: number;
  isVToken0: boolean;
}) => {
  const tick = Number(_tick);
  if (!tick) {
    return 0;
  }
  return tickUpper / tick;
};

export const computeMinPriceInEth = ({
  tickLower,
  isVToken0,
}: {
  tickLower: number;
  isVToken0: boolean;
}) => {
  if (tickLower == null) {
    return '';
  }
  return formatEther(calculatePriceFromTick(tickLower, isVToken0));
};
export const computeMaxPriceInEth = ({
  tickUpper,
  isVToken0,
}: {
  tickUpper: number;
  isVToken0: boolean;
}) => {
  if (tickUpper == null) {
    return '';
  }
  return formatEther(calculatePriceFromTick(tickUpper, isVToken0));
};

export const useComputeAmounts = ({
  amountController,
  ethAmount,
  feeTier,
  liquidityAmount,
  network,
  poolType,
  tokenAddress,
  tokenAmount,
}: {
  amountController: 'ethAmount' | 'tokenAmount';
  ethAmount: string | undefined;
  feeTier: string;
  liquidityAmount: string;
  network: number;
  poolType: 'pair' | 'nft' | 'eth';
  tokenAddress: Address;
  tokenAmount: string | undefined;
}) => {
  const {
    positions: { calculateLiquidity },
  } = useInfrastructure();

  return useCallback(
    async (args: {
      ethAmount?: string;
      tokenAmount?: string;
      minPriceInEth: string;
      maxPriceInEth: string;
      price: string;
      fullRange: boolean;
    }): Promise<{
      ethAmount: string;
      tokenAmount: string;
      liquidityAmount: string;
    }> => {
      // You can either pass in the eth amount or the token amount and calculate the alternative
      // If you pass both, we need to clear one out
      if (args.ethAmount && args.tokenAmount) {
        args.ethAmount = undefined;
        args.tokenAmount = undefined;
      }
      // If you don't pass either, we'll default to the one the user has entered
      if (!args.ethAmount && !args.tokenAmount) {
        if (amountController === 'ethAmount') {
          args.ethAmount = ethAmount;
        } else {
          args.tokenAmount = tokenAmount;
        }
      }

      if (
        !args.minPriceInEth ||
        !args.maxPriceInEth ||
        !args.price ||
        !Number(args.price) ||
        (!args.ethAmount && !args.tokenAmount) ||
        (!Number(args.ethAmount) && !Number(args.tokenAmount))
      ) {
        return {
          ethAmount: ethAmount || '',
          tokenAmount: tokenAmount || '',
          liquidityAmount: liquidityAmount || '',
        };
      }

      // Only pass in the side we want to calculate
      const addLiqAmountA = args.ethAmount
        ? parseEther(args.ethAmount)
        : undefined;
      const addLiqAmountB = args.tokenAmount
        ? parseEther(args.tokenAmount)
        : undefined;

      const { addLiqTokenA, addLiqTokenB, addLiqAmount } =
        await calculateLiquidity({
          network,
          tokenB: tokenAddress,
          feeTier: Number(feeTier) as FeeTier,
          amountALower: parseEther(args.minPriceInEth),
          amountACurrent: parseEther(args.price),
          amountAUpper: parseEther(args.maxPriceInEth),
          addLiqAmountA,
          addLiqAmountB,
          fullRange: args.fullRange,
        });

      let newEthAmount = formatEther(addLiqTokenA);
      let newTokenAmount = formatEther(addLiqTokenB);

      if (args.ethAmount) {
        newEthAmount = args.ethAmount;
      } else if (args.tokenAmount) {
        newTokenAmount = args.tokenAmount;
      }

      switch (poolType) {
        case 'nft':
          newEthAmount = '0';
          break;
        case 'eth':
          newTokenAmount = '0';
          break;
        default:
          break;
      }

      const newLiquidityAmount = formatEther(addLiqAmount);

      return {
        ethAmount: newEthAmount,
        tokenAmount: newTokenAmount,
        liquidityAmount: newLiquidityAmount,
      };
    },
    [
      amountController,
      calculateLiquidity,
      ethAmount,
      feeTier,
      liquidityAmount,
      network,
      poolType,
      tokenAddress,
      tokenAmount,
    ]
  );
};
